import React, { useCallback, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useDisclosure } from '@chakra-ui/hooks';
import jwt from 'jsonwebtoken';

import { deleteFromQueryParams } from '../utils';
import { APP_PROXY_URL } from '../../utils/constants';

export enum DrawerPlacement {
  top = 'top',
  right = 'right',
  bottom = 'bottom',
  left = 'left',
}

interface DrawerOptions {
  placement?: DrawerPlacement;
  withCloseButton?: boolean;
}

interface IGlobalContextProps {
  subscriptionId?: string;
  shopNotifyBySMS?: boolean;
  addonsDiscounts?: AddonsDiscounts;
  addonsSellingPolicy?: 'ONETIME' | 'SUBSCRIPTION' | 'ANY';
  countries?: Country[];
  liquidOrders?: LiquidOrders;
  customerEmail?: string;
  shopDomain?: string;
  shopEmail?: string;
  customerPortalSettings?: CustomerPortalSettings;
  customerId?: string;
  authToken?: string;
  isShopifyLoggedIn?: boolean;
  shopCurrencyCode?: string;
  enableShuffleUpcomingProducts?: boolean;
  isBundleUpdating?: boolean;
  setIsBundleUpdating?: (boolean) => void;
  enableShuffleSkipProducts?: boolean;
  bundleIsReady?: boolean;
  setBundleIsReady?: (boolean) => void;
}

interface IGlobalContext extends IGlobalContextProps {
  setContextValues?: (values: IGlobalContext) => void;
  isDrawerOpen: boolean;
  onDrawerClose: () => void;
  onDrawerOpen: (
    component?: React.ReactElement,
    options?: DrawerOptions,
  ) => void;
  drawerComponent?: React.ReactElement;
  drawerOptions: DrawerOptions;
  setAuthToken: (string) => void;
  setCustomerId: (string) => void;
  logout: () => void;
  loadLocalStorageSession: () => boolean;
  customerPortalSettings?: CustomerPortalSettings;
  shopAddressCountry?: string;
  hideBaBSoldOutOptions?: boolean;
  ignoreBaBOutOfStockAllowance?: boolean;
  shopHasActiveMoments?: boolean;
  shopHasArchivedMoments?: boolean;
  baBEditAutoOpened?: boolean;
  setBaBEditAutoOpened?: (boolean) => void;
}

export const GlobalContext = React.createContext<IGlobalContext>({
  isDrawerOpen: false,
  onDrawerClose: () => undefined,
  onDrawerOpen: () => undefined,
  drawerComponent: undefined,
  drawerOptions: { placement: DrawerPlacement.right, withCloseButton: true },
  setAuthToken: () => undefined,
  setCustomerId: () => undefined,
  logout: () => undefined,
  loadLocalStorageSession: () => false,
  isShopifyLoggedIn: false,
  hideBaBSoldOutOptions: undefined,
  ignoreBaBOutOfStockAllowance: undefined,
  enableShuffleUpcomingProducts: undefined,
  bundleIsReady: true,
  setBundleIsReady: () => undefined,
  shopHasActiveMoments: false,
  shopHasArchivedMoments: false,
});

GlobalContext.displayName = 'GlobalContext';

export const useGlobalContext = (): IGlobalContext =>
  React.useContext<IGlobalContext>(GlobalContext);

export const GlobalContextProvider = ({
  value,
  children,
}: {
  value: IGlobalContextProps;
  children: React.ReactElement;
}): React.ReactElement => {
  const [baBEditAutoOpened, setBaBEditAutoOpened] = useState(false);
  const [isBundleUpdating, setIsBundleUpdating] = useState<boolean>(false);
  const [bundleIsReady, setBundleIsReady] = useState<boolean>(true);
  const [stateValues, setStateValues] = useState<IGlobalContextProps>(
    value || {},
  );
  const [authToken, setAuthToken] = useState<string | undefined>(
    value?.authToken,
  );
  const [customerId, setCustomerId] = useState<string | undefined>(
    value?.customerId,
  );
  const {
    isOpen: isDrawerOpen,
    onClose: onDrawerClose,
    onOpen: onDrawerOpen,
  } = useDisclosure();
  const [drawerComponent, setDrawerComponent] = useState<
    React.ReactElement | undefined
  >();
  const [drawerOptions, setDrawerOptions] = useState<DrawerOptions>({
    placement: DrawerPlacement.right,
    withCloseButton: true,
  });

  const handleOnDrawerClose = () => {
    setDrawerComponent(undefined);
    onDrawerClose();
  };
  const handleOnDrawerOpen = (
    component?: React.ReactElement,
    options: DrawerOptions = {
      placement: DrawerPlacement.right,
      withCloseButton: true,
    },
  ) => {
    setDrawerComponent(component);
    setDrawerOptions(options);
    onDrawerOpen();
  };

  const setContextValues = useCallback(
    (values: IGlobalContext) => {
      setStateValues({ ...stateValues, ...values });
    },
    [stateValues],
  );

  const loadLocalStorageSession = () => {
    if (localStorage) {
      try {
        const apiAuth = JSON.parse(
          localStorage.getItem('awtomicApiAuth') || '',
        );

        if (
          apiAuth?.authToken &&
          apiAuth?.customerId &&
          checkJWTExpiration(apiAuth.authToken)
        ) {
          setAuthToken(apiAuth.authToken);
          setCustomerId(apiAuth.customerId);
          return true;
        }
      } catch (e) {
        // do nothing
      }
    }

    return false;
  };

  useEffect(() => {
    // we load session from local storage only if there is no new authToken in hydrated props
    // or if the user is logged in through shopify
    if (!value.authToken && !value.isShopifyLoggedIn) {
      loadLocalStorageSession();
    }
    deleteFromQueryParams(['authToken', 'customerId']);
  }, []);

  useEffect(() => {
    localStorage &&
      localStorage.setItem(
        'awtomicApiAuth',
        JSON.stringify({ authToken, customerId }),
      );
  }, [authToken, customerId]);

  const logout = () => {
    setAuthToken(undefined);
    setCustomerId(undefined);
    localStorage.setItem('awtomicApiAuth', JSON.stringify({}));

    // This removes the last slash if it exists. eg: /fr/ -> /fr and /fr -> /fr
    const shopifyRoot =
      (window.Shopify?.routes?.root || '/').replace(/(.*)(\/)$/, '$1') || '';
    const url = `${shopifyRoot}${APP_PROXY_URL}`;

    // now redirect back to login so we clear any existing URL params
    window.location.href = url;
  };

  return (
    <GlobalContext.Provider
      value={{
        ...stateValues,
        baBEditAutoOpened,
        setBaBEditAutoOpened,
        setContextValues,
        isDrawerOpen,
        onDrawerClose: handleOnDrawerClose,
        onDrawerOpen: handleOnDrawerOpen,
        drawerComponent,
        drawerOptions,
        customerId,
        setCustomerId,
        authToken,
        setAuthToken,
        logout,
        loadLocalStorageSession,
        isBundleUpdating,
        setIsBundleUpdating,
        bundleIsReady,
        setBundleIsReady,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

const checkJWTExpiration = (token) => {
  const payload = jwt.decode(token) as any;
  const now = DateTime.utc().toSeconds();

  return payload && payload.customerId && payload.exp && payload.exp > now;
};
